import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Empty, Section } from '@easymoving/ui';
import { useBackend } from '../lib/backend/context';
import { useItems } from '../hooks/useUserData';
import { ItemForm, type ItemFormSubmit } from '../components/items/ItemForm';

export function EditItemPage() {
  const { t } = useTranslation();
  const backend = useBackend();
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { items, loading } = useItems();
  const item = items.find((i) => i.id === itemId);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState<string>();

  if (loading) {
    return (
      <Container className="pt-6">
        <p className="text-graphite py-12">{t('common.loading')}</p>
      </Container>
    );
  }
  if (!item || !itemId) {
    return (
      <Container className="pt-10">
        <Empty title={t('notFound.title')} body={t('notFound.body')} />
      </Container>
    );
  }

  async function handleSubmit({ draft, file, removeExisting }: ItemFormSubmit) {
    if (!itemId) return;
    setBusy(true);
    setBusyLabel(t('item.saving'));
    try {
      await backend.updateItem(itemId, draft);
      if (file) {
        setBusyLabel(t('item.uploadingPhoto'));
        await backend.uploadPrimaryImage(itemId, file);
      } else if (removeExisting) {
        await backend.clearPrimaryImage(itemId);
      }
      navigate(`/app/items/${itemId}`);
    } finally {
      setBusy(false);
      setBusyLabel(undefined);
    }
  }

  return (
    <Container size="narrow" className="pt-6">
      <Section title={t('item.editTitle')}>
        <div className="max-w-md mx-auto">
          <ItemForm
            initial={item}
            busy={busy}
            busyLabel={busyLabel}
            submitLabel={t('item.save')}
            onSubmit={handleSubmit}
          />
        </div>
      </Section>
    </Container>
  );
}
